'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import EditablePackageDimensionsTable from './EditablePackageDimensionsTable'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { InfoCircledIcon } from '@radix-ui/react-icons'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { gql, useMutation, useQuery } from '@apollo/client'
import { Icons } from '@/components/shared/icons'
import { useAdditionalComponentDelete } from '@/containers/AdditionalComponentGroups/useAdditionalComponentDelete'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  GET_ADDITIONAL_COMPONENT,
  GET_FABRIC_PRICES,
} from '@/components/queries/additionalComponentQueries'
import { LinkedAdditionalComponentsSection } from './LinkedAdditionalComponentsSection'
import { ImageUpload } from '@/components/shared/inputComponents/ImageUpload'
import FabricCategoryPrices from '@/components/shared/inputComponents/FabricCategoryPrices'
import ExtraPriceInput from '@/components/shared/inputComponents/ExtraPriceInput'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'

const dimensionsConstructor = {
  armrest_height: 'Armrest height',
  armrest_length: 'Armrest length',
  armrest_width: 'Armrest width',
  bedbox_height: 'Bedbox height',
  bedbox_length: 'Bedbox length',
  bedbox_width: 'Bedbox width',
  covered_side: 'Covered side',
  frame_thickness: 'Frame thickness',
  headboard_height: 'Headboard height',
  headboard_width: 'Headboard width',
  height: 'Height',
  leg_height: 'Leg height',
  length: 'Length',
  lying_part_height: 'Lying part height',
  mattress_height: 'Mattress height',
  mattress_length: 'Mattress length',
  mattress_overlay_height: 'Mattress overlay height',
  mattress_width: 'Mattress width',
  radius: 'Radius',
  seat_depth: 'Seat depth',
  seat_height: 'Seat height',
  seat_width: 'Seat width',
  shade_height: 'Shade height',
  shade_radius: 'Shade radius',
  table_extended_lengh: 'Table extended length',
  table_leg_width: 'Table leg width',
  table_top_thickness: 'Table top thickness',
  weight: 'Weight',
  width: 'Width',
}

const UPDATE_ADDITIONAL_COMPONENT = gql`
  mutation UpdateExistingAdditionalComponent(
    $id: Float!
    $advancedProductId: Float
    $additionalComponent: AdditionalComponentCustomInput
  ) {
    updateExistingAdditionalComponent(
      id: $id
      advancedProductId: $advancedProductId
      additionalComponent: $additionalComponent
    ) {
      id
      is_wrapper
      additional_component_profiles {
        id
        name
        language
        description
      }
    }
  }
`

const formSchema = z.object({
  enabled: z.boolean(),
  is_wrapper: z.boolean().optional().default(false),
  component_sku: z.string(),
  code: z.string(),
  dimensions: z
    .record(z.union([z.number(), z.boolean(), z.undefined()]))
    .optional(),
  extra_prices: z
    .array(
      z.object({
        id: z.number().optional(),
        price: z.number().min(0, 'Price must be non-negative'),
        price_listId: z.number(),
      })
    )
    .optional(),
  image: z
    .object({
      src: z.string().optional().nullable(),
      src_md: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  conditions: z.string().transform(str => {
    try {
      return JSON.parse(str)
    } catch {
      return {}
    }
  }),
  metadata: z.string().transform(str => {
    try {
      return JSON.parse(str)
    } catch {
      return {}
    }
  }),
  package_dimensions: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        volume: z.number(),
      })
    )
    .default([]),
  profiles: z.array(
    z.object({
      id: z.number().optional(),
      name: z.string().min(0, 'Name is required'),
      language: z.string(),
      description: z.string().optional().default(''),
    })
  ),
  // .transform((items, ctx) => {
  //   const filteredItems = items.filter((item) => item.name?.trim() !== "");
  //   if (filteredItems.length === 0) {
  //     items.forEach((item, index) => {
  //       ctx.addIssue({
  //         code: z.ZodIssueCode.custom,
  //         message: "At least one language profile needs to be filled",
  //         path: [`[${index}].name`],
  //       });
  //     });
  //   }
  //   return filteredItems;
  // }),
})

interface AdditionalComponentEditFormProps {
  data: {
    id: number
    componentGroupId?: number
    advancedProductId?: number
    enabled?: boolean
    component_sku?: string
    profiles?: Array<{
      id?: number
      name: string
      language: string
    }>
    componentGroup?: {
      use_fabric_prices_for_components: boolean
    }
    manufacturerId?: number
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function AdditionalComponentEditForm({
  data,
  onDirtyChange,
  onRequestClose,
}: AdditionalComponentEditFormProps) {
  const { allLanguages, languageId, language } = useLanguage()
  const [isLoading, setIsLoading] = useState(true)
  const [hasManualEnabledValue, setHasManualEnabledValue] = useState(false)

  const isGroupContext = data.advancedProductId === undefined

  // Guarded backend delete; the dialog shows how many products use it.
  const { confirmDelete } = useAdditionalComponentDelete('component')
  const onDelete = useCallback(() => {
    if (data.id === undefined) return
    confirmDelete([data.id], () => onRequestClose?.())
  }, [confirmDelete, data.id, onRequestClose])

  const { data: componentData } = useQuery(GET_ADDITIONAL_COMPONENT, {
    variables: {
      id: data.id,
      advancedProductId: data.advancedProductId ?? null,
      includeProductData: !isGroupContext,
    },
    onCompleted: () => {
      setIsLoading(false)
    },
    onError: () => {
      setIsLoading(false)
    },
    skip: !data.id,
    fetchPolicy: 'network-only',
  })

  // When there is no id (create mode), clear loading immediately
  useEffect(() => {
    if (!data.id) {
      setIsLoading(false)
    }
  }, [data.id])

  const { manufacturerId, componentGroup } = data
  const use_fabric_prices_for_components =
    componentGroup?.use_fabric_prices_for_components || false

  const additionalComponentData = componentData?.findUniqueAdditionalComponent
  const advancedProductData =
    additionalComponentData?.additional_component_to_advanced_product?.[0]

  // Helper function to filter dimensions based on enabled dimensions in group
  const filterEnabledDimensions = useCallback(
    (dimensions: any, enabledDimensions: string[] = []) => {
      if (!dimensions || !enabledDimensions.length) return {}

      const filtered: any = {}
      enabledDimensions.forEach(key => {
        if (dimensions[key] !== undefined && dimensions[key] !== null) {
          filtered[key] = dimensions[key]
        }
      })
      return filtered
    },
    []
  )

  // Define form values type based on the Zod schema
  type FormValues = z.infer<typeof formSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      enabled: advancedProductData?.enabled || false,
      is_wrapper: additionalComponentData?.is_wrapper ?? false,
      component_sku: additionalComponentData?.component_sku || '',
      code: additionalComponentData?.code || '',
      dimensions: filterEnabledDimensions(
        additionalComponentData?.dimensions,
        additionalComponentData?.additional_component_group
          ?.enabled_dimensions_in_group
      ),
      extra_prices: advancedProductData?.extra_prices || [],
      image: additionalComponentData?.image || { src: '', src_md: '' },
      conditions: JSON.stringify(
        advancedProductData?.conditions || {},
        null,
        2
      ),
      metadata: JSON.stringify(advancedProductData?.metadata || {}, null, 2),
      package_dimensions: additionalComponentData?.package_dimensions || [],
      profiles: allLanguages.map(lang => {
        const existing =
          additionalComponentData?.additional_component_profiles?.find(
            p => p.language === lang
          )
        return {
          id: existing?.id,
          name: existing?.name ?? '',
          language: lang,
          description: existing?.description ?? '',
        }
      }),
    },
  })

  const { data: fabricPricesData } = useQuery(GET_FABRIC_PRICES, {
    variables: { where: { id: data.id } },
    skip: !data.id,
  })

  const [updateComponent] = useMutation(UPDATE_ADDITIONAL_COMPONENT, {
    onError: e => {
      toast.error('Failed to update component')
    },
    onCompleted: () => {
      toast.success('Component updated successfully')
      form.reset(form.getValues())
    },
    refetchQueries: ['GET_PRODUCT_DETAILS'],
  })

  const handleReset = () => {
    const currentMetadata = form.getValues('metadata')
    try {
      const parsedMetadata = JSON.parse(currentMetadata)
      if (parsedMetadata.manualValues) {
        const { enabled, ...restManualValues } = parsedMetadata.manualValues
        const newMetadata = {
          ...parsedMetadata,
          manualValues: restManualValues,
        }
        form.setValue('metadata', JSON.stringify(newMetadata, null, 2), {
          shouldDirty: true,
        })
        // Reset the enabled value to the server value
        form.setValue('enabled', advancedProductData?.enabled || false, {
          shouldDirty: true,
        })
      }
    } catch (error) {
      console.error('Failed to parse metadata:', error)
    }
  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (!additionalComponentData) return

    // Remove __typename recursively
    const removeTypename = (obj: any) => {
      if (obj === null || typeof obj !== 'object') return obj
      const newObj = { ...obj }
      delete newObj.__typename
      Object.keys(newObj).forEach(key => {
        newObj[key] = removeTypename(newObj[key])
      })
      return newObj
    }

    // Process package dimensions to remove temporary IDs for new dimensions
    const processedPackageDimensions = values.package_dimensions.map(
      dimension => {
        // If the ID is a timestamp (temporary ID), remove it for new dimensions
        const isTemporaryId = dimension.id > 1000000000000 // Timestamp-based IDs are much larger
        if (isTemporaryId) {
          const { id, ...dimensionWithoutId } = dimension
          return dimensionWithoutId
        }
        return dimension
      }
    )

    // Replicate the entered name into any blank-language profiles
    const sourceName =
      values.profiles.find(p => p.name?.trim())?.name?.trim() ?? ''
    const normalizedProfiles = values.profiles.map(p => ({
      ...p,
      name: p.name?.trim() ? p.name : sourceName,
    }))

    updateComponent({
      variables: {
        id: data.id,
        advancedProductId: isGroupContext ? null : data.advancedProductId,
        additionalComponent: {
          id: data.id,
          enabled: isGroupContext ? true : values.enabled,
          is_wrapper: values.is_wrapper ?? false,
          component_sku: values.component_sku,
          code: values.code,
          image: [values.image?.src],
          conditions: values.conditions,
          dimensions: values.dimensions || {},
          additional_component_profiles: normalizedProfiles,
          extra_price: 0,
          isDefault: advancedProductData?.isDefault || false,
          metadata: values.metadata,
          package_dimensions: processedPackageDimensions,
        },
      },
    })
  }

  // Track initial mount to prevent marking form as dirty during initialization
  const isInitialMount = useRef(true)

  // Update metadata when enabled changes and differs from server value
  useEffect(() => {
    const currentEnabled = form.watch('enabled')
    const serverEnabled = advancedProductData?.enabled || false

    // Skip the effect during initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Only update if the current value differs from server value
    if (currentEnabled !== serverEnabled) {
      const currentMetadata = form.getValues('metadata')
      let parsedMetadata
      try {
        parsedMetadata = JSON.parse(currentMetadata)
      } catch {
        parsedMetadata = {}
      }

      const newMetadata = {
        ...parsedMetadata,
        manualValues: {
          ...(parsedMetadata.manualValues || {}),
          enabled: currentEnabled,
        },
      }

      form.setValue('metadata', JSON.stringify(newMetadata, null, 2), {
        shouldDirty: true,
      })
    }
  }, [form.watch('enabled'), advancedProductData?.enabled])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  // Check if metadata has manualValues.enabled
  useEffect(() => {
    const currentMetadata = form.getValues('metadata')
    try {
      const parsedMetadata = JSON.parse(currentMetadata)
      setHasManualEnabledValue(
        parsedMetadata?.manualValues?.enabled !== undefined
      )
    } catch {
      setHasManualEnabledValue(false)
    }
  }, [form.watch('metadata')])

  useEffect(() => {
    if (additionalComponentData) {
      const defaultValues = {
        enabled: advancedProductData?.enabled || false,
        is_wrapper: additionalComponentData.is_wrapper ?? false,
        component_sku: additionalComponentData.component_sku || '',
        code: additionalComponentData.code || '',
        dimensions: filterEnabledDimensions(
          additionalComponentData.dimensions,
          additionalComponentData.additional_component_group
            ?.enabled_dimensions_in_group
        ),
        extra_prices: advancedProductData?.extra_prices || [],
        image: additionalComponentData?.image || { src: '', src_md: '' },
        conditions: JSON.stringify(
          advancedProductData?.conditions || {},
          null,
          2
        ),
        metadata: JSON.stringify(advancedProductData?.metadata || {}, null, 2),
        package_dimensions: additionalComponentData?.package_dimensions || [],
        profiles: allLanguages.map(lang => {
          const existing =
            additionalComponentData?.additional_component_profiles?.find(
              p => p.language === lang
            )
          return {
            id: existing?.id,
            name: existing?.name ?? '',
            language: lang,
            description: existing?.description ?? '',
          }
        }),
      }

      // Reset form with keepDirty: false to ensure clean state
      form.reset(defaultValues, { keepDirty: false })
    }
  }, [
    additionalComponentData,
    advancedProductData,
    allLanguages,
    filterEnabledDimensions,
  ])

  if (isLoading) {
    return (
      <SheetContent>
        <div className="flex items-center justify-center h-full">
          Loading...
        </div>
      </SheetContent>
    )
  }

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Edit Component</SheetTitle>
          <span className="text-sm text-muted-foreground">
            Id: {additionalComponentData?.id}
          </span>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              {!isGroupContext && (
                <InputFieldContainer
                  label="Enabled"
                  control={form.control}
                  name="enabled"
                  render={({ field }) => (
                    <FormItem className="p-2">
                      <FormControl>
                        <Switch
                          variant="green"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      {hasManualEnabledValue && (
                        <Tooltip>
                          <TooltipTrigger>
                            <InfoCircledIcon className="h-4 w-4 text-blue-500" />
                          </TooltipTrigger>
                          <TooltipContent className="flex flex-col">
                            <p>
                              This value is not synced with the integration
                              server
                            </p>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-red-500"
                              onClick={e => {
                                e.preventDefault() // Prevent form submission
                                handleReset()
                              }}
                            >
                              Reset
                            </Button>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </FormItem>
                  )}
                />
              )}

              <InputFieldContainer
                label="Image"
                description="Upload component image"
                control={form.control}
                name="image.src"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUpload
                        field={{
                          ...field,
                          value: field.value ? [field.value] : [],
                          onChange: (urls: string[]) =>
                            field.onChange(urls[0] || ''),
                        }}
                        maxImages={1}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Name"
                control={form.control}
                name={`profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter component name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                control={form.control}
                name={`profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Enter component description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="SKU"
                control={form.control}
                name="component_sku"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter component SKU" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Code"
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter component code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator />
              {additionalComponentData?.additional_component_group
                ?.enable_custom_dimensions_for_components && (
                <>
                  {[
                    ...(additionalComponentData.additional_component_group
                      .enabled_dimensions_in_group || []),
                  ]
                    .sort((a, b) => {
                      const labelA =
                        dimensionsConstructor[
                          a as keyof typeof dimensionsConstructor
                        ] || a
                      const labelB =
                        dimensionsConstructor[
                          b as keyof typeof dimensionsConstructor
                        ] || b
                      return labelA.localeCompare(labelB)
                    })
                    .map(dimensionKey => {
                      const dimensionLabel =
                        dimensionsConstructor[
                          dimensionKey as keyof typeof dimensionsConstructor
                        ]
                      if (!dimensionLabel) return null

                      // Handle boolean fields (covered_side) with Switch component
                      if (dimensionKey === 'covered_side') {
                        return (
                          <InputFieldContainer
                            key={dimensionKey}
                            label={dimensionLabel}
                            control={form.control}
                            name={`dimensions.${dimensionKey}`}
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-center gap-2 py-1.5">
                                <FormControl>
                                  <Switch
                                    checked={field.value || false}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )
                      }

                      // Handle numeric fields with Input component
                      return (
                        <InputFieldContainer
                          key={dimensionKey}
                          label={dimensionLabel}
                          control={form.control}
                          name={`dimensions.${dimensionKey}`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    type="number"
                                    placeholder="0"
                                    className="pr-10"
                                    {...field}
                                    value={field.value ?? ''}
                                    onChange={e => {
                                      const value = e.target.value
                                      field.onChange(
                                        value === '' ? null : Number(value)
                                      )
                                    }}
                                  />
                                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-muted-foreground">
                                    cm
                                  </span>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )
                    })}
                </>
              )}

              {!isGroupContext &&
                (use_fabric_prices_for_components ? (
                  <FabricCategoryPrices
                    control={form.control}
                    name="price_fabric_category"
                    additionalComponentId={data.id}
                    advancedProductId={data.advancedProductId!}
                    manufacturerId={manufacturerId!}
                  />
                ) : (
                  <ExtraPriceInput control={form.control} name="extra_prices" />
                ))}

              <InputFieldContainer
                label="Packages"
                control={form.control}
                name="package_dimensions"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <EditablePackageDimensionsTable
                      dimensions={field.value || []}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!isGroupContext && (
                <>
                  <InputFieldContainer
                    label="Conditions (JSON)"
                    control={form.control}
                    name="conditions"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Textarea
                            placeholder="Enter conditions as JSON"
                            className="md:text-xs font-mono"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <InputFieldContainer
                    label="Metadata (JSON)"
                    control={form.control}
                    name="metadata"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Textarea
                            placeholder="Enter metadata as JSON"
                            {...field}
                            className="md:text-xs font-mono"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <div className="flex gap-2">
                {data.id !== undefined && data.componentGroupId !== undefined && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={onDelete}
                    className="shrink-0"
                  >
                    <Icons.trash className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <Button type="submit" className="flex-1">Save Changes</Button>
              </div>
            </div>
          </form>
        </Form>

        <LinkedAdditionalComponentsSection
          componentId={data.id}
          linkedComponents={
            additionalComponentData?.linked_components_source ?? []
          }
          language={language}
          isWrapper={form.watch('is_wrapper') ?? false}
          onIsWrapperChange={value =>
            form.setValue('is_wrapper', value, { shouldDirty: true })
          }
        />
      </SheetScrollableContent>
    </SheetContent>
  )
}
